import { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '../../lib/utils';
import {
  useSyncPharmacyMedicines,
  usePharmacySyncStatus,
  useStopPharmacySync,
} from '../../services/httpServices/queries';
import {
  Pill,
  List,
  Tags,
  FlaskConical,
  Building2,
  LayoutGrid,
  RefreshCw,
  Loader2,
  Square,
  AlertTriangle,
  X,
  Truck,
  ShoppingCart,
  PackageCheck,
  ScrollText,
  Wallet,
} from 'lucide-react';

interface PharmacyLayoutProps {
  children: React.ReactNode;
  title?: string;
  description?: string;
  action?: React.ReactNode;
  header?: React.ReactNode;
}

const tabGroups = [
  {
    label: 'Daily',
    items: [{ path: '/pharmacy', label: 'Sales', icon: ShoppingCart }],
  },
  {
    label: 'Catalog',
    items: [
      { path: '/pharmacy/drugs', label: 'Items & Stock', icon: List },
      { path: '/pharmacy/categories', label: 'Categories', icon: Tags },
      { path: '/pharmacy/generics', label: 'Generics', icon: FlaskConical },
      { path: '/pharmacy/companies', label: 'Companies', icon: Building2 },
      { path: '/pharmacy/racks', label: 'Racks', icon: LayoutGrid },
    ],
  },
  {
    label: 'Purchasing',
    items: [
      { path: '/pharmacy/suppliers', label: 'Suppliers', icon: Truck },
      { path: '/pharmacy/purchases', label: 'Purchase Orders', icon: ShoppingCart },
      { path: '/pharmacy/receipts', label: 'Goods Receipts', icon: PackageCheck },
      { path: '/pharmacy/payables', label: 'Payables', icon: Wallet },
    ],
  },
  {
    label: 'Records',
    items: [{ path: '/pharmacy/ledger', label: 'Stock Ledger', icon: ScrollText }],
  },
];

const tabs = tabGroups.flatMap((group) => group.items);

export function PharmacyLayout({ children, title, description, action, header }: PharmacyLayoutProps) {
  const location = useLocation();
  const queryClient = useQueryClient();
  const syncMedicines = useSyncPharmacyMedicines();
  const stopSync = useStopPharmacySync();
  const [syncId, setSyncId] = useState<string | null>(() =>
    sessionStorage.getItem('pharmacy-sync-id')
  );
  const [syncConfirmOpen, setSyncConfirmOpen] = useState(false);
  const [syncMode, setSyncMode] = useState<'update' | 'replace'>('update');
  const { data: syncStatusData } = usePharmacySyncStatus(syncId || undefined);
  const syncStatus = syncStatusData?.data;

  const isSyncing =
    !!syncId &&
    syncStatus?.state !== 'completed' &&
    syncStatus?.state !== 'failed' &&
    syncStatus?.state !== 'stopped';

  // The medicine sync action is only relevant on the Items & Stock (drugs) page.
  const isDrugsPage = location.pathname === '/pharmacy/drugs';

  const openSyncConfirm = () => {
    if (isSyncing) return;
    setSyncMode('update');
    setSyncConfirmOpen(true);
  };

  const handleSync = async () => {
    if (isSyncing) return;
    setSyncConfirmOpen(false);
    setSyncId(null);
    const response = await syncMedicines.mutateAsync({
      queries: undefined,
      clearExisting: syncMode === 'replace',
    });
    if (response.data?.syncId) {
      setSyncId(response.data.syncId);
    }
  };

  const handleStop = async () => {
    if (!syncId) return;
    await stopSync.mutateAsync(syncId);
    setSyncId(null);
  };

  useEffect(() => {
    if (syncId) {
      sessionStorage.setItem('pharmacy-sync-id', syncId);
    } else {
      sessionStorage.removeItem('pharmacy-sync-id');
    }
  }, [syncId]);

  useEffect(() => {
    if (syncStatus?.state === 'completed') {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-drugs'] });
      queryClient.invalidateQueries({ queryKey: ['pharmacy-drug-categories'] });
      queryClient.invalidateQueries({ queryKey: ['pharmacy-generic-names'] });
      queryClient.invalidateQueries({ queryKey: ['pharmacy-companies'] });
      setSyncId(null);
    }
  }, [syncStatus?.state, queryClient]);

  const activeTabPath = useMemo(() => {
    let best: string | null = null;
    for (const t of tabs) {
      if (location.pathname === t.path || location.pathname.startsWith(`${t.path}/`)) {
        if (!best || t.path.length > best.length) best = t.path;
      }
    }
    return best;
  }, [location.pathname]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        {header ?? (
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Pill className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-text">{title || 'Pharmacy'}</h1>
              {description && <p className="text-sm text-text-muted">{description}</p>}
            </div>
          </div>
        )}
        <div className="flex items-center gap-2">
          {isDrugsPage && (
            <>
              <button
                onClick={openSyncConfirm}
                disabled={isSyncing}
                className="px-4 py-2 bg-surface border border-border text-text rounded-lg hover:bg-background transition-colors text-sm font-medium flex items-center gap-2 shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isSyncing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
                {isSyncing ? 'Syncing...' : 'Sync Medicines'}
              </button>
              {isSyncing && (
                <button
                  onClick={handleStop}
                  disabled={stopSync.isPending}
                  className="px-4 py-2 bg-danger/10 border border-danger/20 text-danger rounded-lg hover:bg-danger/20 transition-colors text-sm font-medium flex items-center gap-2 shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {stopSync.isPending ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Square size={16} fill="currentColor" />
                  )}
                  Stop Sync
                </button>
              )}
            </>
          )}
          {action && <div className="flex items-center gap-2">{action}</div>}
        </div>
      </div>

      {/* Sync progress */}
      {isDrugsPage && syncStatus && (
        <div className="bg-surface rounded-xl border border-border p-4 shadow-sm space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-text font-medium">
              <RefreshCw size={16} className="text-primary" />
              <span>Medicine sync: {syncStatus.state}</span>
            </div>
            <span className="text-sm text-text-muted">
              {syncStatus.processed + syncStatus.failed} / {syncStatus.discovered} medicines
            </span>
          </div>
          <div className="h-2 w-full bg-background rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-all duration-500"
              style={{
                width: `${syncStatus.discovered > 0 ? ((syncStatus.processed + syncStatus.failed) / syncStatus.discovered) * 100 : 0}%`,
              }}
            />
          </div>
          <div className="flex items-center gap-4 text-xs text-text-muted">
            <span>Discovered: {syncStatus.discovered}</span>
            <span>Processed: {syncStatus.processed}</span>
            <span>Failed: {syncStatus.failed}</span>
            <span>Pending: {syncStatus.pending}</span>
          </div>
        </div>
      )}

      {/* Sync confirmation modal */}
      {isDrugsPage && syncConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-surface border border-border rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="flex items-center gap-2">
                <RefreshCw size={18} className="text-primary" />
                <h2 className="text-lg font-semibold text-text">Sync Medicines</h2>
              </div>
              <button
                onClick={() => setSyncConfirmOpen(false)}
                className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-text transition-colors"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-sm text-text-muted">
                This will fetch medicines from the external Medicine API. Choose how existing data should be handled.
              </p>
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => setSyncMode('update')}
                  className={`w-full text-left p-3 border rounded-lg transition-colors ${
                    syncMode === 'update'
                      ? 'border-primary bg-primary/5 ring-1 ring-primary/40'
                      : 'border-border bg-background hover:bg-background/60'
                  }`}
                >
                  <div className="flex items-center gap-2 text-sm font-medium text-text">
                    <RefreshCw size={14} className="text-primary" />
                    Update existing data (Recommended)
                  </div>
                  <p className="text-xs text-text-muted mt-1">
                    Keep all current drugs, categories, generic names and companies. Matching records are updated, new ones are added, and duplicate entries are merged automatically.
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => setSyncMode('replace')}
                  className={`w-full text-left p-3 border rounded-lg transition-colors ${
                    syncMode === 'replace'
                      ? 'border-danger bg-danger/5 ring-1 ring-danger/40'
                      : 'border-border bg-background hover:bg-background/60'
                  }`}
                >
                  <div className="flex items-center gap-2 text-sm font-medium text-text">
                    <AlertTriangle size={14} className="text-warning" />
                    Remove all &amp; re-sync
                  </div>
                  <p className="text-xs text-text-muted mt-1">
                    Delete all existing drugs, categories, generic names and companies before syncing. Use this to start the catalog from scratch.
                  </p>
                </button>
              </div>
              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  onClick={() => setSyncConfirmOpen(false)}
                  className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSync}
                  disabled={syncMedicines.isPending}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-2"
                >
                  {syncMedicines.isPending && <Loader2 size={16} className="animate-spin" />}
                  <RefreshCw size={16} />
                  {syncMode === 'replace' ? 'Remove & Sync' : 'Update Data'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Grouped navigation */}
      <div className="bg-surface border border-border rounded-xl p-2 shadow-sm overflow-x-auto">
        <nav className="flex items-stretch gap-5 min-w-max">
          {tabGroups.map((group) => (
            <div key={group.label} className="flex flex-col gap-1">
              <span className="px-2 text-[10px] font-semibold uppercase tracking-wider text-text-muted">
                {group.label}
              </span>
              <div className="flex items-center gap-1">
                {group.items.map((tab) => {
                  const active = activeTabPath === tab.path;
                  return (
                    <Link
                      key={tab.path}
                      to={tab.path}
                      className={cn(
                        'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap',
                        active
                          ? 'bg-primary text-white shadow-sm'
                          : 'text-text-muted hover:text-text hover:bg-background'
                      )}
                    >
                      <tab.icon size={16} />
                      {tab.label}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
      </div>

      {children}
    </div>
  );
}
